import {
    assembleConstellationDerivations,
    assembleItemDerivations,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { EntityItemCollectionDerivation, SpaceDeriver } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toDirectoryConstellationDerivations } from '@/adapters/core/_pure/derivation/constellations';
import { toOverviewCameraPose, type CoreLayout } from '@/adapters/core/_pure/derivation/coreLayout';
import { deriveRingTree } from '@/adapters/core/_pure/derivation/ringTree';
import { deriveRingTreeRadials } from '@/adapters/core/_pure/derivation/ringTreeRadials';
import { deriveLayeredTree } from '@/adapters/core/_pure/derivation/layeredTree';
import { deriveLayeredConnections } from '@/adapters/core/_pure/derivation/layeredConnections';
import { toNodeItemDerivation } from '@/adapters/core/_pure/derivation/itemFactories';
import { toDirectoryChildCollectionKey, toRootCollectionKey } from '@/adapters/core/_pure/keyCodec';
import { getCoreProjection, type VisualNode } from '@/adapters/core/_pure/projection';
import type { Config, CosmosDataModel } from '@/adapters/core/_contract/config';
import type { RuntimeState } from '@/adapters/core/_contract/config';
import { coreChangeHighlights, corePlannedHighlights } from '@/adapters/core/_pure/derivation/agentChanges';

function toCoreItemCollectionDerivations(
    nodes: readonly VisualNode[],
    config: Config,
    radiusByPath: ReadonlyMap<string, number>,
    explicitLayout?: CoreLayout,
): EntityItemCollectionDerivation[] {
    const highlights = config.agentPlannedFiles ? corePlannedHighlights(nodes, config.agentPlannedFiles)
        : coreChangeHighlights(nodes, config.agentChanges);
    const itemDerivationByPath = new Map(nodes.map((node) => [node.path, toNodeItemDerivation(node, config,
        radiusByPath.get(node.path), explicitLayout?.entryByPath.get(node.path)?.position, highlights.get(node.path))]));
    const rootNode = nodes.find((node) => node.parentPath === null);
    const rootCollection = rootNode === undefined
        ? []
        : [toEntityItemCollectionDerivation({
            itemCollectionKey: toRootCollectionKey(),
            itemDerivations: [itemDerivationByPath.get(rootNode.path)!],
        })];
    const childCollections = nodes.flatMap((node) => {
        if (node.nodeKind !== 'directory' || node.childPaths.length === 0) return [];

        return [toEntityItemCollectionDerivation({
            itemCollectionKey: toDirectoryChildCollectionKey(node.path),
            itemDerivations: node.childPaths.flatMap((path) => {
                const itemDerivation = itemDerivationByPath.get(path);
                return itemDerivation === undefined ? [] : [itemDerivation];
            }),
        })];
    });

    return [...rootCollection, ...childCollections];
}

export function createSpaceDeriver(): SpaceDeriver<
    CosmosDataModel,
    Config,
    RuntimeState
> {
    return (input) => {
        const config = input.config;
        const mode = config.layout ?? 'layered-tree';
        if (mode !== 'layered-tree' && mode !== 'ring-tree') {
            throw new Error(`Unknown Core layout: ${mode}`);
        }
        const projection = getCoreProjection(input.cosmosData);
        const layered = mode === 'layered-tree' ? deriveLayeredTree(projection, config) : undefined;
        const ring = mode === 'ring-tree' ? deriveRingTree(projection, config) : undefined;
        const tree = (layered ?? ring)!;
        const initialCamera = toOverviewCameraPose(tree.initialBoundsRadius, config);
        if (ring && config.ringTree?.orientation === 'standing') {
            initialCamera.position = [0, 0, Math.hypot(...initialCamera.position)];
        }
        if (layered) {
            const distance = Math.max(80, layered.initialBoundsRadius * config.geometry.cameraPadding);
            initialCamera.position = [distance * 0.7, distance * 0.2, distance];
            initialCamera.target = [0, 0, -(config.layeredTree?.levelGap ?? 24) * 2];
        }
        const radials = layered ? deriveLayeredConnections(layered, config)
            : ring?.childRings.length ? deriveRingTreeRadials(ring, config.visualStyle.directoryColor) : undefined;
        const itemAssembly = assembleItemDerivations([
            ...toCoreItemCollectionDerivations(tree.nodes, config, tree.radiusByPath, layered?.layout),
            ...(radials ? [radials.items] : []),
        ]);
        const constellationAssembly = assembleConstellationDerivations(
            [...toDirectoryConstellationDerivations(tree.nodes, config, tree.layout, ring?.rotationByPath, Boolean(layered)),
                ...(radials ? [radials.constellation] : [])],
        );

        return {
            base: {
                spaceKey: input.spaceKey,
                itemCollections: itemAssembly.itemCollections,
                constellations: constellationAssembly.constellations,
                itemParametricState: itemAssembly.itemParametricState,
                constellationParametricState: constellationAssembly.constellationParametricState,
                spaceRenderOptions: {
                    backgroundColor: '#070b18',
                    camera: { pose: initialCamera },
                },
            },
        };
    };
}